import React, { useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { faFile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OtTemplateQuery, SanityOtQuery } from '../../../graphql-types';
import { Section } from '../../components/2.0version/ot/section/Section';
import { Resume } from '../../components/2.0version/ot/section/content/resume/Resume';
import { MapWrapper } from '../../components/2.0version/mapWrapper/MapWrapper';
import { usePageContext } from '../../hooks/contexts/usePageContext';
import { WidgetDispoDiv, WidgetDispoResaInstance, WidgetResaDiv } from '../ingenie/WidgetResa';
import { IframeContact } from '../ingenie/IframeContact';
import { ContactInfos, Social } from '../../components/2.0version/contactInfos/ContactInfos';
import { WidgetReviews } from '../ingenie/WidgetReviews';
import { PricesSection } from '../../components/2.0version/prices/Prices';
import { Openings } from '../../components/2.0version/openings/Openings';
import { checkOpenings } from './OtActivitesHebergements';
import { LocaleLink } from '../../components/2.0version/localeLink/LocaleLink';
import { CarouselMiniatures } from '../../components/2.0version/carouselMiniatures/CarouselMiniatures';
import * as styles from './otBilleterie.module.scss';
import { Button } from '../../components/2.0version/button/Button';
import { Modal } from '../../components/2.0version/modal/Modal';
import { handleOpenItineraryMap } from '../../utils/openItineraryMap';

interface IOtBilleterieProps {
  ot?: OtTemplateQuery['ingenieOt'];
}

const contactExists = (contact) =>
  contact.address || contact.email || contact.tel || contact.webSite || contact.mainLocation;

export const OtBilleterie = ({ ot }: IOtBilleterieProps) => {
  const { lang } = usePageContext();
  const { sanityObjetTouristique, allSanityListingSelection, sanityTranslations } =
    useStaticQuery<SanityOtQuery>(QUERY);
  const [open, setOpenModal] = useState(false);
  const usableCodeForIngenieWidget = ot?.codePrestation ?? ot?.codePrestataire;
  const reviewsTripadvisor = ot?.liens?.filter((attr) => attr?.type === 'TRIPADVISOR');

  const descriptions: any = [];
  const includes: any = [];

  const wantedDesc = [
    'Le tarif comprend',
    'Le tarif ne comprend pas',
    'Le prix comprend',
    'Le prix ne comprend pas',
    'Infos Pratiques',
  ];

  ot?.descriptifs?.forEach((descriptif, i) => {
    if (!wantedDesc.includes(descriptif?.libelle ?? '') && descriptif?.contenu?.[lang]) {
      descriptions.push(descriptif?.contenu?.[lang]);
    } else if (descriptif?.contenu?.[lang]) {
      includes.push({
        idx: i,
        libelle: descriptif?.libelle,
        content: descriptif?.contenu?.[lang],
      });
    }
  });

  const getDocument = (docs) => {
    const doc = docs?.find((d: any) => d.langue === lang);
    return doc || docs[0];
  };

  const carouselItems: any = ot?.medias?.map((media) => {
    const alt = media?.attributs?.find((attr) => attr?.type === 'ALT');
    return {
      _key: media?.id,
      url: media?.url,
      alt,
      type: 'image',
    };
  });

  /** put first image at the end */
  carouselItems.push(carouselItems.shift());

  const printWidgetResa = !allSanityListingSelection.nodes
    .filter((rubrique) =>
      ot?.parentRubriques?.find(
        (parentRubrique) => parentRubrique?.slug === rubrique?.slug?.current
      )
    )
    .find((rubrique) => rubrique.hideWidgetResa);

  const printWidgetDispo = !allSanityListingSelection.nodes
    .filter((rubrique) =>
      ot?.parentRubriques?.find(
        (parentRubrique) => parentRubrique?.slug === rubrique?.slug?.current
      )
    )
    .find((rubrique) => rubrique.hideWidgetDispo);

  return (
    <div className={styles.wrapper}>
      <div className={styles.grid}>
        <div className={styles.content}>
          <div className={styles.descriptionBlock}>
            <p
              dangerouslySetInnerHTML={{ __html: descriptions.join('<br/><br/>') }}
              className={styles.description}
            />
            {includes?.length > 0 && (
              <div>
                <br />
                {includes.map((include) => {
                  let libelle = '';

                  if (lang === 'en') {
                    if (
                      include.libelle?.toLowerCase() === 'Le prix ne comprend pas'.toLowerCase() ||
                      include.libelle?.toLowerCase() === 'Le tarif ne comprend pas'.toLowerCase()
                    )
                      libelle = "Price doesn't include";
                    else if (
                      include.libelle?.toLowerCase() === 'Le prix comprend'.toLowerCase() ||
                      include.libelle?.toLowerCase() === 'Le tarif comprend'.toLowerCase()
                    )
                      libelle = 'Price include';
                    else if (include.libelle?.toLowerCase() === 'Infos Pratiques'.toLowerCase())
                      libelle = 'Practical informations';
                  } else libelle = include.libelle;

                  return (
                    <>
                      <p
                        key={include.idx}
                        className={styles.libelle}
                        dangerouslySetInnerHTML={{ __html: libelle }}
                      />
                      <p dangerouslySetInnerHTML={{ __html: include.content }} />
                      <br />
                    </>
                  );
                })}
              </div>
            )}
          </div>
          <div className={styles.carouselMiniatures}>
            <CarouselMiniatures items={carouselItems} />
          </div>

          {checkOpenings(ot?.ouvertures) && (
            <Section title={sanityObjetTouristique?._rawOpenings[lang]}>
              <Openings openings={ot?.ouvertures} />
            </Section>
          )}
          {ot?.content?.resume && ot?.content?.resume?.length > 0 && (
            <Section title={sanityObjetTouristique?._rawResume[lang]}>
              <Resume data={ot?.content?.resume} />
            </Section>
          )}
          {ot?.tarifs && ot?.tarifs.length > 0 && (
            <Section title={sanityObjetTouristique?._rawPrices[lang]}>
              <PricesSection prices={ot?.tarifs} />
            </Section>
          )}
          {ot?.reservable && usableCodeForIngenieWidget && (
            <Section
              title={sanityObjetTouristique?._rawDisponibilities[lang]}
              hidden={!printWidgetDispo}
            >
              <WidgetDispoDiv codePrestation={usableCodeForIngenieWidget} />
            </Section>
          )}
          {ot?.reservable && usableCodeForIngenieWidget && (
            <Section className={styles.reviews} title={sanityObjetTouristique?._rawReviews[lang]}>
              <WidgetReviews codePrestation={usableCodeForIngenieWidget} />
            </Section>
          )}
          {!ot?.reservable &&
            reviewsTripadvisor &&
            reviewsTripadvisor.length > 0 &&
            usableCodeForIngenieWidget && (
              <Section title={sanityObjetTouristique?._rawReviews[lang]}>
                <LocaleLink target="_blank" to={reviewsTripadvisor?.[0]?.url}>
                  {reviewsTripadvisor?.[0]?.url}
                </LocaleLink>
              </Section>
            )}
          {ot?.displayableContact &&
            ot?.codePrestataire &&
            contactExists(ot?.displayableContact) && (
              <Section
                classNameChildren={styles.contactSection}
                title={sanityObjetTouristique?._rawContact[lang]}
              >
                <ContactInfos
                  address={ot?.displayableContact?.address ?? null}
                  tel={ot?.displayableContact?.tel ?? ''}
                  webSite={ot.reservable ? '' : ot?.displayableContact?.webSite ?? ''}
                  socials={ot?.socials as Social[]}
                  mainLocation={ot?.displayableContact?.mainLocation ?? ''}
                  mainLocationLibelle={ot?.displayableContact?.mainLocationLibelle?.[lang] ?? ''}
                />
                {ot.displayableContact?.email && (
                  <>
                    <Button onClick={() => setOpenModal(true)}>
                      {`${sanityTranslations?._rawContactLabel?.[lang]} ${ot?.libelle?.fr}`}
                    </Button>
                    <Modal fullScreenMobile open={open} setOpenModal={setOpenModal}>
                      <div className={styles.contactWrapper}>
                        <IframeContact codePrestataire={ot?.codePrestataire ?? ''} />
                      </div>
                    </Modal>
                  </>
                )}
              </Section>
            )}
          {ot?.documents && ot?.documents?.length > 0 && (
            <Section
              classNameChildren={styles.contactSection}
              title={sanityObjetTouristique?._rawDocuments?.[lang]}
            >
              <a href={getDocument(ot?.documents)?.url} target="_blank" rel="noreferrer">
                {getDocument(ot?.documents)?.libelle}
                <FontAwesomeIcon icon={faFile} className={styles.icon} />
              </a>
            </Section>
          )}
          {ot?.localisation?.gps && (
            <Section title={sanityObjetTouristique?._rawMap[lang]}>
              <Button onClick={() => handleOpenItineraryMap(ot)}>
                {sanityTranslations?._rawMapLabel?.[lang]}
              </Button>
            </Section>
          )}
        </div>
        {ot?.reservable && usableCodeForIngenieWidget && (
          <div className={`${styles.widgetWrapper} ${!printWidgetResa ? styles.hidden : ''}`}>
            <div className={styles.widget}>
              <WidgetResaDiv codePrestation={usableCodeForIngenieWidget} />
            </div>
          </div>
        )}
      </div>
      {ot?.reservable && usableCodeForIngenieWidget && (
        <WidgetDispoResaInstance codePrestation={usableCodeForIngenieWidget} />
      )}
    </div>
  );
};

export const QUERY = graphql`
  query SanityOt {
    sanityObjetTouristique {
      _rawPracticalInformations
      _rawResume
      _rawDetails
      _rawPrices
      _rawContact
      _rawDisponibilities
      _rawReviews
      _rawMap
      _rawDocuments
      _rawOpenings
    }

    allSanityListingSelection {
      nodes {
        hideWidgetResa
        hideWidgetDispo
        slug {
          current
        }
      }
    }

    sanityTranslations {
      _rawContactLabel
      _rawMapLabel
    }
  }
`;
